import { useMutation, useQuery } from 'convex/react'
import { useState } from 'react'
import { api } from '@backend/_generated/api'
import type { Id } from '@backend/_generated/dataModel'
import type { TrajectoryResponsePositionTrajectoriesItem } from '@/types/api-compat'
import type { PositionAssessmentTimeseriesItem } from '../types'
import { useMyConvexOrganizationId } from '@/features/auth'

/**
 * Returns all data needed to render the current user's dashboard.
 *
 * Wraps the Convex `dashboard.getMyDashboard` query. No userId argument is
 * needed — identity is resolved server-side from the auth session.
 *
 * The returned `trajectorySteps` are cast to TrajectoryResponsePositionTrajectoriesItem
 * so the existing TrajectoryPathVisualization presentational component can be reused.
 */
export const useMyDashboardData = () => {
  const raw = useQuery(api.dashboard.getMyDashboard)

  if (raw === undefined) {
    return { data: undefined, isLoading: true }
  }

  if (raw === null) {
    return { data: null, isLoading: false }
  }

  const trajectorySteps = raw.trajectory.steps
    .filter((s) => s.fromPosition !== null)
    .map((s) => ({
      ...s,
      fromPosition: s.fromPosition!,
      toPosition: s.toPosition ?? null,
    })) as unknown as Array<TrajectoryResponsePositionTrajectoriesItem>

  return {
    data: {
      sinceDate: raw.sinceDate,
      positionLevel: raw.positionLevel,
      trajectory: {
        name: raw.trajectory.name,
        steps: trajectorySteps,
      },
    },
    isLoading: false,
  }
}

/**
 * Maps a Convex assessment average to the PositionAssessmentTimeseriesItem
 * shape expected by processTimeseries / processLatestData chart utils.
 *
 * Convex stores averages as numbers; the old API returned them as strings.
 * parseFloat() in the chart utils handles string→number, and String()
 * here handles number→string so both paths stay compatible.
 */
function mapToTimeseriesItem(
  avg: NonNullable<
    ReturnType<typeof useQuery<typeof api.dashboard.getMyAssessmentAverages>>
  >[number],
): PositionAssessmentTimeseriesItem {
  const toAvgString = (v: number | undefined): string | null =>
    v !== undefined ? String(v) : null

  // completedAt is a unix timestamp (ms). Extract the date-only string so
  // processTimeseries can group entries by date, matching old API behaviour.
  const completedAtDate = new Date(avg.completedAt).toISOString().split('T')[0]!

  return {
    id: avg._id,
    assessmentResponseId: avg.assessmentResponseId,
    assessmentId: avg.assessmentId,
    userId: avg.userId,
    responseType: avg.responseType,
    overallAvg: toAvgString(avg.overallAvg),
    talentAvg: toAvgString(avg.talentAvg),
    craftAvg: toAvgString(avg.craftAvg),
    resultsAvg: toAvgString(avg.resultsAvg),
    directionAvg: toAvgString(avg.directionAvg),
    cultureAvg: toAvgString(avg.cultureAvg),
    completedAt: completedAtDate,
    createdAt: completedAtDate,
    updatedAt: completedAtDate,
    assessmentCreatedAt: completedAtDate,
  }
}

/**
 * Returns assessment averages for the currently authenticated user, mapped to
 * the PositionAssessmentTimeseriesItem shape used by the chart utilities.
 *
 * @param latest - When true, returns only the most recent self + manager rows.
 */
export const useMyAssessmentAverages = (
  latest?: boolean,
): {
  data: Array<PositionAssessmentTimeseriesItem> | undefined
  isLoading: boolean
  error: Error | undefined
} => {
  const result = useQuery(api.dashboard.getMyAssessmentAverages, {
    latest: latest ?? false,
  })

  if (result === undefined) {
    return { data: undefined, isLoading: true, error: undefined }
  }

  return {
    data: result.map(mapToTimeseriesItem),
    isLoading: false,
    error: undefined,
  }
}

/**
 * Returns profile and trajectory data for any user by their Convex user _id.
 *
 * Used by the direct report profile pages where directReportId is a Convex _id.
 */
export const useDirectReportProfile = (userId: string | undefined) => {
  const orgId = useMyConvexOrganizationId()
  const raw = useQuery(
    api.dashboard.getUserProfile,
    userId && orgId
      ? {
          userId: userId as Id<'users'>,
          organizationId: orgId as Id<'organizations'>,
        }
      : 'skip',
  )

  if (raw === undefined) {
    return { data: undefined, isLoading: true, error: undefined }
  }

  if (raw === null) {
    return { data: null, isLoading: false, error: undefined }
  }

  const trajectorySteps = (raw.trajectory?.steps ?? [])
    .filter((s) => s.fromPosition !== null)
    .map((s) => ({
      ...s,
      fromPosition: s.fromPosition!,
      toPosition: s.toPosition ?? null,
    })) as unknown as Array<TrajectoryResponsePositionTrajectoriesItem>

  return {
    data: {
      id: raw.id,
      name: raw.name ?? '',
      email: raw.email ?? '',
      avatar: raw.image ?? null,
      position: raw.position,
      trajectory: raw.trajectory
        ? { name: raw.trajectory.name, steps: trajectorySteps }
        : null,
      manager: raw.manager,
      organization: raw.organization,
    },
    isLoading: false,
    error: undefined,
  }
}

/**
 * Returns assessment averages for any user by their Convex user _id,
 * mapped to the PositionAssessmentTimeseriesItem shape used by chart utilities.
 *
 * @param userId - Convex user _id of the target user
 * @param latest - When true, returns only the most recent self + manager rows
 */
export const useDirectReportAssessmentAverages = (
  userId: string | undefined,
  latest?: boolean,
): {
  data: Array<PositionAssessmentTimeseriesItem> | undefined
  isLoading: boolean
  error: Error | undefined
} => {
  const result = useQuery(
    api.dashboard.getAssessmentAveragesForUser,
    userId
      ? { userId: userId as Id<'users'>, latest: latest ?? false }
      : 'skip',
  )

  if (result === undefined) {
    return { data: undefined, isLoading: true, error: undefined }
  }

  return {
    data: result.map(mapToTimeseriesItem),
    isLoading: false,
    error: undefined,
  }
}

// ── Convex-backed mutations ────────────────────────────────────────────────────

/**
 * Replaces the user's org roles using the Convex `updateUserRoles` mutation.
 * The org ID is resolved from the auth store.
 */
export const useConvexUpdateUserRoles = () => {
  const orgId = useMyConvexOrganizationId()
  const updateRoles = useMutation(api.organization.updateUserRoles)
  const [isPending, setIsPending] = useState(false)

  return {
    isPending,
    mutateAsync: async ({
      userId,
      roleIds,
    }: {
      userId: string
      roleIds: string[]
    }) => {
      if (!orgId) throw new Error('Organization not found')
      setIsPending(true)
      try {
        await updateRoles({
          userId: userId as Id<'users'>,
          organizationId: orgId as Id<'organizations'>,
          roleIds: roleIds as Id<'organizationRoles'>[],
        })
      } finally {
        setIsPending(false)
      }
    },
  }
}

/**
 * Changes the manager for an employee using the Convex `changeUserManager` mutation.
 * The org ID is resolved from the auth store.
 */
export const useConvexChangeUserManager = () => {
  const orgId = useMyConvexOrganizationId()
  const changeManager = useMutation(api.organization.changeUserManager)
  const [isPending, setIsPending] = useState(false)

  return {
    isPending,
    mutateAsync: async ({
      userId,
      managerId,
    }: {
      userId: string
      managerId: string
    }) => {
      if (!orgId) throw new Error('Organization not found')
      setIsPending(true)
      try {
        await changeManager({
          employeeUserId: userId as Id<'users'>,
          newManagerUserId: managerId as Id<'users'>,
          organizationId: orgId as Id<'organizations'>,
        })
      } finally {
        setIsPending(false)
      }
    },
  }
}

/**
 * Soft-removes a user from the organization using the Convex `removeUser` mutation.
 */
export const useConvexRemoveUser = () => {
  const removeUser = useMutation(api.organization.removeUser)
  const [isPending, setIsPending] = useState(false)

  return {
    isPending,
    mutateAsync: async ({
      userId,
      organizationId,
      reason,
    }: {
      userId: string
      organizationId: string
      reason?: string
    }) => {
      setIsPending(true)
      try {
        await removeUser({
          userId: userId as Id<'users'>,
          organizationId: organizationId as Id<'organizations'>,
          reason,
        })
      } finally {
        setIsPending(false)
      }
    },
  }
}

/**
 * Returns the current user's own profile for the Settings page.
 * Replaces the Hono `useDetailedUser` call in settings/index.tsx.
 */
export const useMyProfile = () => {
  const raw = useQuery(api.dashboard.getMyProfile)

  if (raw === undefined) {
    return { data: undefined, isLoading: true }
  }

  if (raw === null) {
    return { data: null, isLoading: false }
  }

  return {
    data: {
      id: raw.id,
      name: raw.name ?? '',
      email: raw.email ?? '',
      phone: raw.phone ?? null,
      avatar: raw.image ?? null,
      username: null as null,
      position: raw.position,
      manager: raw.manager,
    },
    isLoading: false,
  }
}

/**
 * Returns the list of auth providers linked to the current user's account.
 * Provider values: "github" | "google" | "resend-otp"
 */
export const useMyLinkedProviders = () => {
  const raw = useQuery(api.profile.getMyLinkedProviders)
  return {
    providers: raw?.map((a) => a.provider) ?? [],
    isLoading: raw === undefined,
  }
}

/**
 * Updates the current user's own profile (name, phone, avatar).
 * When avatar is set, marks customImage=true to prevent OAuth sign-ins
 * from overwriting it.
 */
export const useUpdateMyProfile = () => {
  const updateProfile = useMutation(api.profile.updateMyProfile)
  const [isPending, setIsPending] = useState(false)

  return {
    isPending,
    mutateAsync: async (data: {
      name?: string
      phone?: string
      image?: string
    }) => {
      setIsPending(true)
      try {
        await updateProfile(data)
      } finally {
        setIsPending(false)
      }
    },
  }
}
